/**
 * Searchable contractor combobox for profit-center staff.
 *
 * Renders the currently selected contractor (or a "select" prompt) and opens
 * a type-ahead list of the PC's affiliated contractors. Used both as the
 * prominent picker in browse-first mode and as the compact "Ordering for"
 * switcher in the order flow header.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { PcContractor } from "@/components/pc/PcOrderScopeProvider";

interface ContractorPickerProps {
  contractors: PcContractor[];
  value: PcContractor | null;
  onSelect: (contractor: PcContractor) => void;
  isLoading?: boolean;
  /** Compact styling for the order-flow header. */
  compact?: boolean;
}

export function ContractorPicker({
  contractors,
  value,
  onSelect,
  isLoading = false,
  compact = false,
}: ContractorPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          size={compact ? "sm" : "default"}
          className={cn(
            "justify-between",
            compact ? "max-w-[240px]" : "w-full max-w-sm",
          )}
          disabled={isLoading}
        >
          <span className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <span className="truncate">
              {value
                ? value.name
                : isLoading
                  ? t("components.pc.contractorPicker.loading")
                  : t("components.pc.contractorPicker.placeholder")}
            </span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 flex-shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t("components.pc.contractorPicker.searchPlaceholder")}
          />
          <CommandList>
            <CommandEmpty>
              {t("components.pc.contractorPicker.noResults")}
            </CommandEmpty>
            <CommandGroup>
              {contractors.map((contractor) => (
                <CommandItem
                  key={contractor.orgId}
                  value={contractor.name}
                  onSelect={() => {
                    setOpen(false);
                    if (contractor.orgId !== value?.orgId) {
                      onSelect(contractor);
                    }
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value?.orgId === contractor.orgId
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <div className="min-w-0">
                    <div className="truncate">{contractor.name}</div>
                    {contractor.hajocaCustomerId && (
                      <div className="text-xs text-muted-foreground">
                        {contractor.hajocaCustomerId}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
